import React, { useMemo, useState } from "react"

import { Empty, Flex, Spin, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../../Components/Button"
import Private from "../../../../Components/Private"
import SelectComponent from "../../../../Components/Select"
import TableComponent from "../../../../Components/Table"
import Images from "../../../../Images"
import { byReasonCodeEmptyData } from "../../utils/emptyData"
import HealthBanner from "../HealthBanner"
import { getColumnsByReasonCode, ReasonCodeDescription } from "../Table/byReasonCodeColumns"
import { formatAmount, formatCount } from "../Table/columns"
import { createTableSummary } from "../Table/tableSummary"

const { Title, Text } = Typography

const ByReasonCodeBlock = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { search } = useLocation()
  const theme = useSelector((state) => state.nav.theme)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const dataByCardTypeFetching = useSelector((state) => state.health.dataByReasonCodeFetching)
  const dataByReasonCode = useSelector((state) => state.health.dataByReasonCode)
  const { is_business_health_available } = useSelector((state) => state.health.mainInfo)
  const [mobileColumn, setMobileColumn] = useState("type")

  const { results, summary, comparison_period, comparison_summary } = is_business_health_available
    ? dataByReasonCode
    : byReasonCodeEmptyData
  const comparisonLabel = comparison_period?.label

  const columns = useMemo(() => getColumnsByReasonCode({ t, theme }), [t, theme])

  const tableSettings = useMemo(
    () => ({
      scroll: results?.length > 0 ? { x: "auto", y: isMobile ? 350 : undefined } : {}
    }),
    [results, isMobile]
  )

  const tableSummary = useMemo(() => createTableSummary(t), [t])

  const tableData = useMemo(() => (Array.isArray(results) ? results.slice(0, 5) : []), [results])
  const hasTableData = tableData.length > 0

  const mobileColumnOptions = useMemo(
    () => [
      { label: t("Type"), value: "type" },
      { label: t("Chargebacks"), value: "chargebacks" },
      { label: t("Chargebacks without us"), value: "chargebacks_without_us" },
      { label: t("Alerts"), value: "alerts" },
      { label: t("Prevention rate"), value: "prevention_rate" }
    ],
    [t]
  )

  const selectedMobileColumnLabel =
    mobileColumnOptions.find((option) => option.value === mobileColumn)?.label || t("Column")

  const navigateToFullTable = () => {
    navigate(`breakdown_by_reason_code${search}`)
  }

  const renderMobileValueCell = (record) => {
    switch (mobileColumn) {
      case "type":
        return <Text strong>{record?.type}</Text>
      case "chargebacks":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(record?.chargebacks_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.chargebacks_amount)}</Private>
            </Text>
          </Flex>
        )
      case "chargebacks_without_us":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(record?.chargebacks_count_without_us)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.chargebacks_amount_without_us)}</Private>
            </Text>
          </Flex>
        )
      case "alerts":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(record?.alerts_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.alerts_amount)}</Private>
            </Text>
          </Flex>
        )
      case "prevention_rate":
        return <Text strong>{record?.prevention_rate}%</Text>
      default:
        return null
    }
  }

  const renderMobileSummaryValue = (data) => {
    switch (mobileColumn) {
      case "type":
        return <Text type="secondary">-</Text>
      case "chargebacks":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(data?.chargebacks_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(data?.chargebacks_amount)}</Private>
            </Text>
          </Flex>
        )
      case "chargebacks_without_us":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(data?.chargebacks_count_without_us)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(data?.chargebacks_amount_without_us)}</Private>
            </Text>
          </Flex>
        )
      case "alerts":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(data?.alerts_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(data?.alerts_amount)}</Private>
            </Text>
          </Flex>
        )
      case "prevention_rate":
        return <Text strong>{data?.prevention_rate || 0}%</Text>
      default:
        return null
    }
  }

  return (
    <Flex gap={12} vertical={true} className="reason-codes-block-wrap">
      <Flex gap={24} vertical={true} className="card-block-with-table reason-codes-block">
        <Spin spinning={dataByCardTypeFetching}>
          <Flex
            align="center"
            justify="space-between"
            style={{ marginBottom: 16, padding: "0 16px" }}
          >
            <Title level={5} style={{ marginBottom: 0, fontWeight: 600 }}>
              {t("Breakdown by Reason Code")}
            </Title>

            {is_business_health_available && !isMobile && (
              <ButtonComponent onClick={navigateToFullTable}>{t("View All")}</ButtonComponent>
            )}
          </Flex>

          {isMobile ? (
            <Flex vertical={true} gap={12} className="by-reason-code-mobile-table">
              <div className="by-reason-code-mobile-table__select-wrap">
                <SelectComponent
                  size="large"
                  value={mobileColumn}
                  onChange={setMobileColumn}
                  options={mobileColumnOptions}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="by-reason-code-mobile-table__header">
                <Text type="secondary">{t("Reason code")}</Text>
                <Text type="secondary" className="by-reason-code-mobile-table__header-right">
                  {selectedMobileColumnLabel}
                </Text>
              </div>

              {hasTableData ? (
                <>
                  {tableData.map((record, index) => {
                    const image = Images?.[record?.card_brand?.toLowerCase()] || null
                    const isLastRow = index === tableData.length - 1

                    return (
                      <div
                        key={record.reason_code}
                        className={cls("by-reason-code-mobile-table__row", {
                          "by-reason-code-mobile-table__row--last": isLastRow
                        })}
                      >
                        <Flex
                          gap={8}
                          align="flex-start"
                          className="by-reason-code-mobile-table__left"
                        >
                          {image && (
                            <img src={image} alt={record.reason_code} width={20} height={20} />
                          )}
                          <Flex
                            gap={2}
                            vertical
                            className="by-reason-code-mobile-table__reason-wrap"
                          >
                            <Text strong>{record.reason_code}</Text>
                            <ReasonCodeDescription description={record?.tooltip || []} />
                          </Flex>
                        </Flex>
                        <div className="by-reason-code-mobile-table__right">
                          {renderMobileValueCell(record)}
                        </div>
                      </div>
                    )
                  })}
                  <div className="by-reason-code-mobile-table__summary">
                    <Text strong className="by-reason-code-mobile-table__summary-title">
                      {t("Summary")}
                    </Text>
                    <div className="by-reason-code-mobile-table__right">
                      {renderMobileSummaryValue(summary)}
                    </div>
                  </div>
                  {comparison_summary && (
                    <div className="by-reason-code-mobile-table__summary by-reason-code-mobile-table__summary--compare">
                      <Text strong className="by-reason-code-mobile-table__summary-title">
                        {t("Compare")} {comparisonLabel || t("previous period")}
                      </Text>
                      <div className="by-reason-code-mobile-table__right">
                        {renderMobileSummaryValue(comparison_summary)}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="by-reason-code-mobile-table__empty">
                  <Empty
                    image={Images.no_data}
                    imageStyle={{ width: 64, height: 40 }}
                    description={t("No Data")}
                  />
                </div>
              )}
            </Flex>
          ) : (
            <TableComponent
              columns={columns}
              rowKey={"reason_code"}
              loading={dataByCardTypeFetching}
              dataSource={tableData}
              scroll={tableSettings.scroll}
              showSizeChanger={false}
              withOutPagination={true}
              className="health-breakdown-by-reason-code-table"
              summary={() =>
                tableSummary(
                  summary,
                  results,
                  [
                    [{ name: "type", format: "blank" }],
                    [
                      { name: "chargebacks_count", format: "count" },
                      { name: "chargebacks_amount", format: "currency" }
                    ],
                    [
                      { name: "chargebacks_count_without_us", format: "count" },
                      {
                        name: "chargebacks_amount_without_us",
                        format: "currency"
                      }
                    ],
                    [
                      { name: "alerts_count", format: "count" },
                      { name: "alerts_amount", format: "currency" }
                    ],
                    [{ name: "prevention_rate", format: "percent" }]
                  ],
                  comparison_summary,
                  comparisonLabel
                )
              }
            />
          )}
        </Spin>
        <HealthBanner />
      </Flex>

      {is_business_health_available && isMobile && (
        <ButtonComponent
          className="reason-codes-view-all-btn"
          style={{ width: "100%" }}
          onClick={navigateToFullTable}
        >
          {t("View All")}
        </ButtonComponent>
      )}
    </Flex>
  )
}

export default React.memo(ByReasonCodeBlock)
